import React, { useCallback, useEffect } from "react";
import { AnswerModel, answerModelOf } from "./AnswerModel";
import { useLazyQuery } from "@apollo/client";
import { captureException } from "@sentry/react";
import { ChatToolType } from "_graphql-types-frontend/graphql";
import { useRangerChatStore } from "./chatStore";
import {
  LOAD_CONVERSATION,
  MUTATION_PLANS_BY_CONVERSATION,
  CONVERSATION_ARTIFACTS,
} from "./graphql";
import { ChatMessage, ToolCallEntry } from "../ChatBox/types";
import { speakerMessages } from "./agentSpeakers";
import { ToolsBlock } from "./toolDisplays";
import { MutationPlanCardConnected } from "../MutationPlan/MutationPlanCardConnected";

/** One saved question/answer pair as stored by the conversation history. */
export interface SavedHistoryEntry {
  id: number;
  question: string;
  answer: string;
  createDate: string;
  chatContext?: string | null;
}

function parseToolsFromChatContext(chatContext?: string | null): Array<{
  toolId: string;
  tool: string;
  toolName?: string | null;
  params: any;
  results: any;
}> {
  if (!chatContext) return [];
  try {
    const parsed = JSON.parse(chatContext);
    return Array.isArray(parsed.tools) ? parsed.tools : [];
  } catch (e) {
    captureException(e, {
      tags: { feature: "rangerChat:parseToolsFromChatContext" },
    });
    return [];
  }
}

/** A saved conversation as chat messages: each question and answer with its
 * tools, agents' replies and model, then the conversation's mutation plans. */
export function savedConversationMessages(
  rawHistory: SavedHistoryEntry[],
  plans: Array<{ id: number }>,
  artifacts: Array<{ artifactName: string; downloadUrl: string }>
): ChatMessage[] {
  // Synthetic AtlasTool entry carrying fresh presigned URLs for all artifacts
  // in this conversation. ChatMarkdown resolves atlas-file:<name> links by
  // looking up the name in this entry's outputFiles.
  const atlasToolEntry: ToolCallEntry | null = artifacts.length > 0
    ? {
        toolId: "history-artifacts",
        tool: ChatToolType.AtlasTool,
        params: {},
        results: {
          outputFiles: artifacts.map((a: { artifactName: string; downloadUrl: string }) => ({
            name: a.artifactName,
            downloadUrl: a.downloadUrl,
          })),
        },
      }
    : null;

  // Deduplicate adjacent history entries that share the same question+answer
  // (caused by a prior bug that saved conversation history twice per message).
  // Keep the LAST of each duplicate group since it has richer chatContext (tool results).
  const history = rawHistory.filter((entry, idx) => {
    const next = rawHistory[idx + 1];
    if (next && entry.question === next.question && entry.answer === next.answer) return false;
    return true;
  });

  const messages: ChatMessage[] = history.flatMap(
    (entry: SavedHistoryEntry, idx: number) => {
      const tools = parseToolsFromChatContext(entry.chatContext);
      const items: ChatMessage[] = [
        {
          type: "user" as const,
          data: { type: "markdown" as const, message: entry.question },
        },
      ];

      // Restore the tools display block — ToolsBlock handles the regular
      // tools vs sub-agent spawn partition internally, matching the live
      // stream path in streamEventToHistory.
      if (tools.length > 0) {
        items.push({
          type: "agent" as const,
          id: `history-tools-${idx}`,
          data: {
            type: "react" as const,
            message: <ToolsBlock tools={tools} keyPrefix={`history-${idx}`} />,
          },
        });
      }

      // Persistent agents replied in their own voice; restore that from
      // the same persisted spawn results the tool card uses.
      items.push(...speakerMessages(tools, `history-${idx}`));

      const chartTools = tools.filter(t => t.tool === ChatToolType.ChartTool);
      const hasAtlasLink = entry.answer.includes("atlas-file:");
      const answerTools = [
        ...chartTools,
        ...(hasAtlasLink && atlasToolEntry ? [atlasToolEntry] : []),
      ];
      items.push({
        type: "agent" as const,
        data: answerTools.length > 0
          ? { type: "markdown" as const, message: entry.answer, tools: answerTools }
          : { type: "markdown" as const, message: entry.answer },
      });

      const answeredBy = answerModelOf(entry.chatContext);
      if (answeredBy) {
        items.push({
          type: "agent" as const,
          id: `history-model-${idx}`,
          data: { type: "react" as const, message: <AnswerModel {...answeredBy} /> },
        });
      }

      return items;
    }
  );

  // Append mutation plan cards for this conversation
  for (const plan of plans) {
    messages.push({
      type: "agent" as const,
      id: `mutation-plan-${plan.id}`,
      data: {
        type: "react" as const,
        message: <MutationPlanCardConnected planId={plan.id} />,
      },
    });
  }

  return messages;
}

/**
 * Load a saved conversation into the chat and make it the active slot.
 *
 * Extracted from the history tab because it now has two callers: that tab, and
 * the chat panel itself when something outside (the notification bell) asks for
 * a conversation. Driving it from the tab alone meant the chat's settings
 * popover had to be open for a notification link to do anything — which made
 * the link silently do nothing whenever it wasn't.
 */
export function useLoadConversation() {
  const [loadConversation] = useLazyQuery(LOAD_CONVERSATION, {
    fetchPolicy: "no-cache",
  });
  const [loadMutationPlans] = useLazyQuery(MUTATION_PLANS_BY_CONVERSATION, {
    fetchPolicy: "no-cache",
  });
  const [loadArtifacts] = useLazyQuery(CONVERSATION_ARTIFACTS, {
    fetchPolicy: "no-cache",
  });
  const switchToConversation = useRangerChatStore(s => s.switchToConversation);
  const newChat = useRangerChatStore(s => s.newChat);
  const patchActiveSlot = useRangerChatStore(s => s.patchActiveSlot);
  const setMenuOpen = useRangerChatStore(s => s.setMenuOpen);

  return useCallback(
    async (targetConversationId: number) => {
    try {
      // If we already have a slot for this conversation (live or previously
      // visited), just switch to it — all per-slot state (chatHistory, draft
      // userText, queuedMessage, sessionId) is preserved automatically.
      if (switchToConversation(targetConversationId)) {
        setMenuOpen(false);
        return;
      }

      // No slot for this conversation yet: create a fresh slot and load the
      // saved history into it. The new slot's status stays "idle" since the
      // conversation isn't live.
      newChat();

      // Non-generating conversation: load from DB
      const [result, plansResult, artifactsResult] = await Promise.all([
        loadConversation({ variables: { conversationId: targetConversationId } }),
        loadMutationPlans({ variables: { conversationId: targetConversationId } }),
        loadArtifacts({ variables: { conversationId: targetConversationId } }),
      ]);
      const rawHistory = (result.data?.loadConversation ?? []) as SavedHistoryEntry[];
      const plans = plansResult.data?.mutationPlansByConversation ?? [];
      const artifacts = artifactsResult.data?.conversationArtifacts ?? [];

      const messages = savedConversationMessages(rawHistory, plans, artifacts);

      patchActiveSlot({
        chatHistory: messages,
        conversationId: targetConversationId,
      });
      setMenuOpen(false);
    } catch (e) {
      // Guards the WHOLE load, not just the network call. The history, plan and
      // artifact processing after that call is where a malformed response or a
      // bad entry would throw, and both callers invoke this without awaiting —
      // so a failure there used to become an unhandled rejection: a blank new
      // chat, nothing in Sentry, and nothing telling the user their click
      // failed. A network timeout was handled better than a bug.
      captureException(e, {
        tags: { feature: "rangerChat:conversationHistory" },
        extra: { conversationId: targetConversationId },
      });
      patchActiveSlot({
        conversationId: targetConversationId,
        chatHistory: [
          {
            type: "agent" as const,
            data: {
              type: "markdown" as const,
              message: "_Failed to load conversation. Please try again._",
            },
          },
        ],
      });
      setMenuOpen(false);
    }
    },
    [
      loadConversation,
      loadMutationPlans,
      loadArtifacts,
      switchToConversation,
      newChat,
      patchActiveSlot,
      setMenuOpen,
    ]
  );
}

/**
 * Fill a slot from its saved conversation when the turn's stream went silent
 * (issue 5605). The stream has no replay, so a `stop` sent while the socket
 * was reconnecting is gone; the server saves the turn either way. Resolves
 * true when the conversation's latest saved question is the one this turn
 * asked, i.e. the lost turn was saved, and false when it is not there (yet).
 */
export function useRecoverTurn() {
  const [loadConversation] = useLazyQuery(LOAD_CONVERSATION, { fetchPolicy: "no-cache" });
  const [loadMutationPlans] = useLazyQuery(MUTATION_PLANS_BY_CONVERSATION, { fetchPolicy: "no-cache" });
  const [loadArtifacts] = useLazyQuery(CONVERSATION_ARTIFACTS, { fetchPolicy: "no-cache" });
  const setSlotChatHistory = useRangerChatStore(s => s.setSlotChatHistory);

  return useCallback(
    async (sessionId: string, question: string): Promise<boolean> => {
      const slot = useRangerChatStore.getState().slots.get(sessionId);
      if (!slot?.conversationId) return false;
      const variables = { conversationId: slot.conversationId };
      const [result, plansResult, artifactsResult] = await Promise.all([
        loadConversation({ variables }),
        loadMutationPlans({ variables }),
        loadArtifacts({ variables }),
      ]);
      // Lazy queries resolve with their error rather than rejecting.
      const error = result.error ?? plansResult.error ?? artifactsResult.error;
      if (error) throw error;
      const saved = (result.data?.loadConversation ?? []) as SavedHistoryEntry[];
      // ponytail: asking the same question twice in a row reads as saved; a
      // turn id on chat's response would settle it if that matters.
      if (saved.at(-1)?.question.trim() !== question.trim()) return false;
      setSlotChatHistory(
        sessionId,
        savedConversationMessages(
          saved,
          plansResult.data?.mutationPlansByConversation ?? [],
          artifactsResult.data?.conversationArtifacts ?? []
        )
      );
      return true;
    },
    [loadConversation, loadMutationPlans, loadArtifacts, setSlotChatHistory]
  );
}

/**
 * Run `load` when something outside the chat asks for a conversation.
 *
 * A hook rather than an inline effect so the wiring is testable on its own: the
 * bug this fixed was precisely that the request was consumed somewhere that
 * often was not mounted, which no test would have noticed.
 */
export function usePendingConversation(load: (id: number) => void) {
  const pendingConversationId = useRangerChatStore(
    s => s.pendingConversationId
  );
  const consumePendingConversationId = useRangerChatStore(
    s => s.consumePendingConversationId
  );

  useEffect(() => {
    if (pendingConversationId === null) return;
    const id = consumePendingConversationId();
    if (id !== null) load(id);
    // `load` is stable (useCallback over store actions); re-running on its
    // identity would re-open the conversation on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingConversationId]);
}
